"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Wrench, RefreshCw, AlertTriangle, Home } from "lucide-react"
import { motion } from "framer-motion"
import Link from "next/link"

interface MaintenancePageProps {
    message?: string
    showRetry?: boolean
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.2,
        },
    },
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" as const },
    },
}

const iconVariants = {
    hidden: { opacity: 0, scale: 0.5, rotate: -45 },
    visible: {
        opacity: 1,
        scale: 1,
        rotate: 0,
        transition: {
            type: "spring" as const,
            stiffness: 200,
            damping: 15,
        },
    },
}

const pulseVariants = {
    animate: {
        scale: [1, 1.05, 1],
        transition: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut" as const,
        },
    },
}

export function MaintenancePage({
    message = "Our services are temporarily unavailable. Please try again later.",
    showRetry = true
}: MaintenancePageProps) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 p-4">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                <Card className="max-w-md w-full border-emerald-200 bg-white/80 backdrop-blur-sm shadow-xl shadow-emerald-100/50">
                    <CardContent className="p-8 text-center">
                        <motion.div
                            variants={iconVariants}
                            className="h-24 w-24 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-200/50"
                        >
                            <motion.div
                                variants={pulseVariants}
                                animate="animate"
                            >
                                <Wrench className="h-12 w-12 text-emerald-600" />
                            </motion.div>
                        </motion.div>

                        <motion.h1
                            variants={itemVariants}
                            className="text-2xl font-bold mb-2 text-gray-800"
                        >
                            Under Maintenance
                        </motion.h1>

                        <motion.p
                            variants={itemVariants}
                            className="text-gray-600 mb-6"
                        >
                            {message}
                        </motion.p>

                        <motion.div
                            variants={itemVariants}
                            className="flex items-center justify-center gap-2 text-sm text-emerald-600 mb-6"
                        >
                            <motion.div
                                animate={{ rotate: [0, -10, 10, -10, 0] }}
                                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
                            >
                                <AlertTriangle className="h-4 w-4" />
                            </motion.div>
                            <span>We apologize for the inconvenience</span>
                        </motion.div>

                        {showRetry && (
                            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3">
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex-1"
                                >
                                    <Link href="/" className="block">
                                        <Button
                                            className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-200/50"
                                        >
                                            <Home className="h-4 w-4 mr-2" />
                                            Go Home
                                        </Button>
                                    </Link>
                                </motion.div>
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex-1"
                                >
                                    <Button
                                        variant="outline"
                                        onClick={() => window.location.reload()}
                                        className="w-full border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 text-emerald-700 hover:text-emerald-800 shadow-sm"
                                    >
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                            className="mr-2"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                        </motion.div>
                                        Try Again
                                    </Button>
                                </motion.div>
                            </motion.div>
                        )}

                        <motion.div
                            variants={itemVariants}
                            className="mt-6 pt-6 border-t border-emerald-200"
                        >
                            <p className="text-xs text-gray-500">
                                TokenKickstarter - Decentralized Token Launchpad
                            </p>
                        </motion.div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    )
}
